import { AsyncPipe, DatePipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AssetDto, AssetVersionDto } from '@nusign/picflow-backend-types';
import { BadgeComponent } from '../../../components/badge/badge.component';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { getFullPath } from '../../../lib/get-full-path';
import { AssetService } from '../../../services/asset.service';
import { ToastService } from '../../../services/toast.service';
import { UploadService } from '../../../services/upload.service';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';

@Component({
  selector: 'app-asset-version-menu',
  templateUrl: './asset-version-menu.component.html',
  styleUrls: ['./asset-version-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    SvgComponent,
    UpperCasePipe,
    DatePipe,
    AssetVersionUrlPipe,
    AsyncPipe,
    LoadingIndicatorComponent,
    BadgeComponent,
    ButtonDirective,
    SvgComponent,
  ],
})
export class AssetVersionMenuComponent implements OnInit {
  private assetService = inject(AssetService);
  private route = inject(ActivatedRoute);
  private check = inject(ChangeDetectorRef);
  private toasts = inject(ToastService);
  private uploadService = inject(UploadService);
  @Input() accessLevel: GalleryAccessLevel;
  @Input() showFooter: boolean;
  @Input() asset: AssetDto;
  @Input() versions: AssetVersionDto[];
  @Input() selectedVersion: AssetVersionDto;
  @Input() otherSelectedVersion: AssetVersionDto;
  @Input() small = false;
  @Output() onSelect = new EventEmitter<AssetVersionDto>();
  @Output() onClose = new EventEmitter<void>();
  names: { [versionId: string]: { name: string; ext: string } };
  urlPrefix = '../';
  loading = signal(false);
  @ViewChild('versionUpload') versionUploadEl: ElementRef;

  constructor() {
    const path = getFullPath(this.route.snapshot);
    if (!path.includes('version')) this.urlPrefix = 'version';
  }

  async ngOnInit() {
    if (!this.selectedVersion) {
      this.selectedVersion = this.asset.version_data;
    }

    this.names = {};
    this.names[this.asset.version_data.id] = this.calcName(this.asset.version_data);
    this.loading.set(true);
    const versions = await this.assetService.getAssetVersions(this.asset.id, <string>this.asset.gallery);
    this.versions = versions;
    this.versions.sort((a, b) => (a.created_at > b.created_at ? -1 : 1));
    versions.forEach(v => (this.names[v.id] = this.calcName(v)));
    this.loading.set(false);
    this.check.detectChanges();
  }

  private calcName(version: AssetVersionDto) {
    const name = version.asset_name.split('.');
    const ext = name.pop();
    return {
      name: name.join('.'),
      ext,
    };
  }

  uploadAssetEvent(target: EventTarget) {
    const files = (target as HTMLInputElement).files;
    this.uploadVersion(Array.from(files));
    this.versionUploadEl.nativeElement.value = '';
  }

  async uploadVersion(_files: File[]) {
    const files = Array.from(_files);
    if (!files.length) return;
    if (files.length > 1) return this.toasts.add('Please upload only one asset', 'error');
    const file = files[0];
    const resp = await this.uploadService.handleCanUpload(files);
    if (!resp) return;

    this.onClose.emit();

    await this.uploadService.uploadNewVersion(this.asset, file);
    await this.uploadService.waitForAssetProcessing(this.asset.id);
  }
}
